import { extendDictionary } from '@/__core__/localization/localization'
import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { defineStore } from 'pinia'
import { TIMELINE_FALLBACK_EVENTS } from './EventDetails.constants'
import { initialDataAdapter } from '@/__core__/components/headers/common-header/CommonHeader.helpers'
import { useFixturesStore } from '@/widgets/fixtures/Fixtures.store'
import { preparePeriodsWithTitleAsNumber } from '@/__core__/helpers/Common.helpers'

/** @typedef {{ eventId: string, sportType: string, languageIdentifier: string, event: FixturesEvent }} Payload */
export const useEventDetailsStore = defineStore('eventDetails', {
  state: () => ({
    event: {},
    timeline: {
      tickerData: TIMELINE_FALLBACK_EVENTS,
      showMatchEvents: false
    },
    initial: {},
    statistics: {},
    drfRetryCounter: 0
  }),

  getters: {
    getUpcomingEventStartTime() {
      return this.event?.upcomingEventStartTime
    },
    getIsUpcoming() {
      return this.event?.upcomingEventStartTime?.isUpcoming
    },
    getHomeTeam() {
      return this.event.headerData?.home
    },
    getAwayTeam() {
      return this.event.headerData?.away
    },
    getMatchStatus() {
      return this.event.matchStatus
    },

    // when event details type is stats these getters will be used
    homeTeam: state => state.event?.headerData?.home,
    awayTeam: state => state.event?.headerData?.away,

    periods: state => state.initial?.matchResultsPerPeriod?.perPeriod,

    homeStats: state => state.statistics?.home?.teamStatistics,
    awayStats: state => state.statistics?.away?.teamStatistics,

    /* init fixtures store and check if opened event is still live */
    fixturesStore() {
      return useFixturesStore()
    },
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    }
  },
  actions: {
    getShouldCallApi() {
      const activeEvent = this.fixturesStore.getEvent(this.event.id)
      const isLive = activeEvent.live

      this.updateEvent(activeEvent)
      return isLive
    },

    /** @param {Payload} payload */
    async fetchEventDetailsData({ event, ...payload }) {
      try {
        /* it is required to check if event is upcoming in order to avoid fetching timeline data because scoreboard sub-widget will be loaded */
        const isUpcoming = this.updateEvent(event)

        /* if event is upcoming scoreboard widget will be opened instead of key moments */
        if (isUpcoming) {
          return false
        }

        if (payload.sportType === 'football') {
          const [responseLocalization, timelineData] = await this.fetchEventDetailsPerSportType(payload)

          this.updateTimeline(timelineData)
          extendDictionary(responseLocalization?.data || {})
        } else if (payload.sportType === 'basketball') {
          const [responseLocalization, initialData, statisticsData] = await this.fetchEventDetailsPerSportType(payload)

          this.updateStatistics(statisticsData, initialData)
          extendDictionary(responseLocalization?.data || {})
        }

        return this.event.live
      } catch (error) {
        throw new Error(`[EventDetails.store, fetchEventDetailsData]: ${error.message}`)
      }
    },

    async scheduleEventDetailsRefresh(payload) {
      SportsModeServiceRepository.Timeline.get(payload)
        .then(response => this.updateTimeline(response.data))
        .catch(() => {
          this.drfRetryCounter++
        })

      /* on each refresh hydrate event with one from fixtures */
      this.updateEvent(this.fixturesStore.getEvent(this.event.id))
    },

    // this will be invoked on basketball events
    async scheduleGameStatsRefresh(payload) {
      try {
        const [initialData, statisticsData] = await Promise.all([SportsModeServiceRepository.Initial.get(payload), SportsModeServiceRepository.Statistics.get(payload)])

        this.updateStatistics(statisticsData, initialData)
      } catch (error) {
        this.drfRetryCounter++
      }
      this.updateEvent(this.fixturesStore.getEvent(this.event.id))
    },

    async fetchEventDetailsPerSportType(payload) {
      switch (payload.sportType) {
        case 'football':
          return Promise.all([
            SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'live-ticker' }),
            SportsModeServiceRepository.Timeline.get(payload)
          ]).then(responses => responses.map(response => response.data))
        case 'basketball':
          // REFACTOR
          payload.eventId = payload.region === 'nba' ? payload.eventId.replace('sr:sport_event', 'sr:match') : payload.eventId

          return Promise.all([
            SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'statistics' }),
            SportsModeServiceRepository.Initial.get(payload),
            SportsModeServiceRepository.Statistics.get(payload)
          ]).then(responses => responses.map(response => response.data))
        default:
          throw new Error(`[EventDetails.nrStore, fetchEventDetailsPerSportType]: unknown sport type ${payload.sportType}`)
      }
    },

    updateStatistics(statisticsData, initialData) {
      if (initialData.sportType === 'basketball') {
        this.initial = {
          ...initialData,
          matchResultsPerPeriod: {
            perPeriod: preparePeriodsWithTitleAsNumber(initialData)
          }
        }
      } else {
        this.initial = initialData
      }

      this.statistics = statisticsData.statistics
    },

    updateTimeline(timeline) {
      const showMatchEvents = timeline.showMatchEvents
      const tickerData = timeline.tickerData.reduce((events, event) => {
        if (event.type === 'substitution') {
          return events
        } else if (event.type === 'yellow_red_card') {
          event.svgId = 'yellow-red-card'
        }

        events.push(event)

        return events
      }, [])

      this.timeline = { showMatchEvents, tickerData }
    },

    updateEvent(newEvent) {
      const adaptedEvent = initialDataAdapter('fixtures')(newEvent)
      const isUpcoming = adaptedEvent.upcomingEventStartTime.isUpcoming

      this.event = adaptedEvent

      return isUpcoming
    }
  }
})
